import { useSystemStatus } from '@/api/status/useSystemStatus';
import { cn } from '@/lib/utils';
import {
  WarningCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  InfoIcon,
} from '@phosphor-icons/react';
import { StatusPageErrorBoundary } from '@/components/error-boundary/StatusPageErrorBoundary';

const StatusPageContent = () => {
  const { data: response, isLoading, error } = useSystemStatus();

  console.log('Status data:', response); // Temporary debug log

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <WarningCircleIcon className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h1 className="text-2xl font-semibold mb-2">Unable to Load Status</h1>
          <p className="text-muted-foreground">Please try again later</p>
        </div>
      </div>
    );
  }

  if (!response?.current) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <ClockIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-semibold mb-2">
            Collecting System Data
          </h1>
          <p className="text-muted-foreground">
            We are gathering initial system health data.
            <br />
            Please check back in a few minutes.
          </p>
        </div>
      </div>
    );
  }

  const status = response.current;

  const getStatusIcon = (serviceStatus: 'healthy' | 'unhealthy') => {
    return serviceStatus === 'healthy' ? (
      <CheckCircleIcon className="h-5 w-5 text-green-500" />
    ) : (
      <WarningCircleIcon className="h-5 w-5 text-destructive" />
    );
  };

  const getStatusColor = (serviceStatus: 'healthy' | 'unhealthy') => {
    return serviceStatus === 'healthy' ? 'bg-green-500' : 'bg-destructive';
  };

  const getStatusMessage = () => {
    if (status.status === 'healthy') {
      return "We're fully operational";
    }
    return "We're experiencing some issues";
  };

  // Sort history from oldest to newest
  const sortedHistory = [...(response.history || [])].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto py-12 px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-6">System Status</h1>
          <div className="bg-card rounded-lg p-6 max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-2">
              {getStatusIcon(status.status)}
              <span className="text-xl font-medium">{getStatusMessage()}</span>
            </div>
            <p className="text-muted-foreground text-sm text-center">
              Last checked: {new Date(status.timestamp).toLocaleString()}
            </p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">System status</h2>
              {response.metrics.uptimeLastDay !== null && (
                <span className="text-sm text-muted-foreground">
                  {response.metrics.uptimeLastDay.toFixed(2)}% uptime
                </span>
              )}
            </div>

            <div className="space-y-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-medium">Overall System</span>
                  <button className="text-muted-foreground hover:text-foreground">
                    <InfoIcon className="h-4 w-4" />
                  </button>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden flex">
                  {sortedHistory.length > 0 ? (
                    sortedHistory.map((check, i) => (
                      <div
                        key={check.timestamp}
                        className={cn(
                          'h-full',
                          getStatusColor(check.status),
                          i === 0 ? 'rounded-l-full' : '',
                          i === sortedHistory.length - 1
                            ? 'rounded-r-full'
                            : '',
                        )}
                        style={{ width: `${100 / sortedHistory.length}%` }}
                        title={`${new Date(
                          check.timestamp,
                        ).toLocaleString()} - ${check.status}`}
                      />
                    ))
                  ) : (
                    <div className="text-sm text-muted-foreground py-2">
                      No historical data available yet
                    </div>
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-medium">Database</span>
                  <button className="text-muted-foreground hover:text-foreground">
                    <InfoIcon className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-muted-foreground ml-auto">
                    {status.databaseResponseTime
                      ? `${status.databaseResponseTime}ms`
                      : 'N/A'}
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden flex">
                  {sortedHistory.length > 0 ? (
                    sortedHistory.map((check, i) => (
                      <div
                        key={check.timestamp}
                        className={cn(
                          'h-full',
                          getStatusColor(check.databaseStatus),
                          i === 0 ? 'rounded-l-full' : '',
                          i === sortedHistory.length - 1
                            ? 'rounded-r-full'
                            : '',
                        )}
                        style={{ width: `${100 / sortedHistory.length}%` }}
                        title={`${new Date(
                          check.timestamp,
                        ).toLocaleString()} - ${check.databaseStatus} (${
                          check.databaseResponseTime
                        }ms)`}
                      />
                    ))
                  ) : (
                    <div className="text-sm text-muted-foreground py-2">
                      No historical data available yet
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="text-center text-sm text-muted-foreground">
            {response.metrics.totalChecks} checks performed in the last 24 hours
          </div>
        </div>
      </div>
    </div>
  );
};

const StatusPage = () => {
  return (
    <StatusPageErrorBoundary>
      <StatusPageContent />
    </StatusPageErrorBoundary>
  );
};

export default StatusPage;
